/* eslint class-methods-use-this: 0 */
import { cloneDeep } from 'lodash';
import Api from 'api/v2/Api';
import axios from 'axios';

/**
 * The CollectionApi class is a base class that provides methods to work with a
 * RESTful API. Child classes can extend this class, specifying a base URL and
 * default parameters and then use the inherited methods. This class is intended
 * to provide a consistent interface so that Api subclasses can be used
 * interchangeably by clients. Common functionality implemented here
 * includes methods to: create(), fetch(), get(), update() and delete() items.
 */
export default class CollectionApi extends Api {
  constructor(
    baseUrl,
    defaultParams,
    maxFetchLimit = 100,
    usesPageTokens = false,
    usesSkipTokens = false,
  ) {
    super();

    if (baseUrl) {
      this.baseUrl = baseUrl;
    } else {
      throw new Error('The baseUrl parameter is required when creating an Api instance!');
    }

    if (defaultParams) {
      this.defaultParams = defaultParams;
    } else {
      throw new Error('The defaultParams parameter is required when creating an Api instance!');
    }

    this.maxFetchLimit = maxFetchLimit;
    this.usesPageTokens = usesPageTokens;
    this.usesSkipTokens = usesSkipTokens;
  }

  /**
   * Given a bound items URL and a list of IDs make a GET request to the bound
   * items endpoint to determine if any of the specified IDs are bound items.
   * Note that the list of IDs is iterated through in batches of 100. Once all
   * bound items have been retrieved, return both the bound item IDs and the
   * bound items themselves, for potential use by the caller.
   */
  checkForBoundItems(url, ids) {
    let boundItemIds = [];
    let boundItems = [];
    const sliceSize = 100;
    const totalSlices = Math.ceil(ids.length / sliceSize);
    const mesg = 'Failed to check for bound items!';

    return new Promise((resolve, reject) => {
      if (ids.length === 0) {
        resolve([]);
        return;
      }

      const checkIdSlice = (sliceNum) => {
        const start = (sliceNum - 1) * sliceSize;
        const end = ((sliceNum - 1) * sliceSize) + sliceSize;
        const currentIdsSlice = ids.slice(start, end);
        const params = {
          filter: `object_id:in:${currentIdsSlice.join('|')}`,
          limit: currentIdsSlice.length,
        };

        this.initialize()
          .then(() => {
            axios
              .get(`${url}?${this.param(params)}`)
              .then((resp) => {
                if (resp && Array.isArray(resp.data)) {
                  boundItemIds = boundItemIds.concat(resp.data.map(item => item.id));
                  boundItems = boundItems.concat(resp.data);

                  if (sliceNum < totalSlices) {
                    checkIdSlice(sliceNum + 1);
                  } else {
                    resolve({ boundItemIds, boundItems });
                  }
                } else {
                  Api.handleError(reject, mesg);
                }
              })
              .catch((error) => {
                Api.handleError(reject, mesg, error);
              });
          })
          .catch((error) => {
            Api.handleError(reject, mesg, error);
          });
      };

      checkIdSlice(1);
    });
  }

  create(item) {
    return new Promise((resolve, reject) => {
      const mesg = 'Unable to create item!';

      this.initialize()
        .then(() => {
          axios
            .post(this.baseUrl, item)
            .then(resp => resolve(resp.data))
            .catch((error) => {
              Api.handleError(reject, mesg, error);
            });
        })
        .catch((error) => {
          Api.handleError(reject, mesg, error);
        });
    });
  }

  /**
   * A general axios delete request function for deleting an item by id.
   * @param  {String} id [The id of the object that will be deleted]
   * @return {Object}    [A promise]
   */
  delete(id) {
    return new Promise((resolve, reject) => {
      const mesg = 'Unable to delete item!';
      this.initialize()
        .then(() => {
          axios
            .delete(`${this.baseUrl}/${id}`)
            .then(resp => resolve(resp.data))
            .catch((error) => {
              Api.handleError(reject, mesg, error);
            });
        })
        .catch((error) => {
          Api.handleError(reject, mesg, error);
        });
    });
  }

  /**
   * @todo Handle /api/v2 responses which return the total count in the
   * X-Total-Count HTTP header.
   */
  fetch(skip, limit, sort, searchTerm, cancelToken, filters = [], pageToken, skipToken) {
    const params = {
      ...cloneDeep(this.defaultParams),
      skip,
      limit,
      sort,
      pageToken,
      skipToken,
    };

    // If sortIgnoreCase is defined, remove the case sensitive sort parameter.
    if (params.sortIgnoreCase) {
      delete params.sort;
    }

    params.filter = this.setFilterTerm(params, filters, searchTerm);
    this.setSearchTerm(params, searchTerm);

    return new Promise((resolve, reject) => {
      const mesg = 'Could not get items!';

      this.initialize()
        .then(() => {
          axios
            .get(`${this.baseUrl}?${this.param(params)}`, {
              cancelToken,
            })
            .then((resp) => {
              if (resp && Array.isArray(resp.data)) {
                const results = resp.data;
                const totalCount = parseInt(resp.headers['x-total-count'], 10);
                resolve({ results, totalCount });
              } else if (resp && typeof resp.data === 'object') {
                resolve(resp.data);
              } else {
                Api.handleError(reject, mesg);
              }
            })
            .catch((error) => {
              this.handleFetchError(reject, mesg, error);
            });
        })
        .catch((error) => {
          Api.handleError(reject, mesg, error);
        });
    });
  }

  fetchAll(searchTerm, boundItems, filters = []) {
    const params = { ...cloneDeep(this.defaultParams) };
    delete params.skip;
    delete params.limit;
    delete params.sort;

    params.filter = this.setFilterTerm(params, filters, searchTerm);
    this.setSearchTerm(params, searchTerm);

    return new Promise((resolve, reject) => {
      this.initialize()
        .then(() => {
          const url = `${this.baseUrl}?${this.param(params)}`;
          const mesg = 'Unable to fetch all items!';

          resolve(this.getAllItems(url, { skip: 0, limit: this.maxFetchLimit }, mesg));
        }).catch((error) => {
          Api.handleError(reject, 'Unable to get items!', error);
        });
    });
  }

  fetchTotalCount(url = this.baseUrl) {
    return super.getAllItemsCount(url);
  }

  get(id, cancelToken) {
    const mesg = 'Unable to get item!';

    return new Promise((resolve, reject) => {
      this.initialize()
        .then(() => {
          axios
            .get(`${this.baseUrl}/${id}`, { cancelToken })
            .then((resp) => {
              if (resp && resp.data) {
                resolve(resp.data);
              } else {
                Api.handleError(reject, mesg);
              }
            })
            .catch((error) => {
              Api.handleError(reject, mesg, error);
            });
        })
        .catch((error) => {
          Api.handleError(reject, mesg, error);
        });
    });
  }

  /**
   * Make a GET request to the bound items URL to get the total count of bound
   * items. Unfortunately, when passing a list of IDs to filter on the bound
   * items endpoint doesn't return the total count of bound items in the HTTP
   * headers, so a separate GET request is required.
   */
  getBoundItemCount(url) {
    return new Promise((resolve, reject) => {
      const mesg = 'Failed to get bound item count!';
      const params = {
        limit: 1,
        skip: 0,
      };

      this.initialize()
        .then(() => {
          axios
            .get(`${url}?${this.param(params)}`)
            .then((resp) => {
              if (resp && resp.headers && resp.headers['x-total-count']) {
                resolve(parseInt(resp.headers['x-total-count'], 10));
              } else {
                // A missing X-Total-Count header is treated as an error
                // when making a GET request to the bound items endpoint because
                // it should be returned!

                Api.handleError(reject, mesg);
              }
            })
            .catch((error) => {
              Api.handleError(reject, mesg, error);
            });
        })
        .catch((error) => {
          Api.handleError(reject, mesg, error);
        });
    });
  }

  getSearchField() {
    return {
      field: undefined,
      value: undefined,
    };
  }

  handleFetchError(reject, mesg, error) {
    if (axios.isCancel(error)) {
      return;
    }
    Api.handleError(reject, mesg, error);
  }

  update(item) {
    const mesg = 'Unable to update item!';
    const id = item._id || item.id;

    return this.initialize()
      .then(() => axios.put(`${this.baseUrl}/${id}`, item))
      .then((resp) => {
        if (resp && resp.data) return resp.data;

        return Error('No data in update response.');
      })
      .catch((error = Error(mesg)) => {
        if (error && error.response && error.response.data && error.response.data.error) {
          throw Error(error.response.data.error);
        }

        const newError = Api.handleError(
          null,
          error.mesg || mesg,
          error,
        );

        throw (newError instanceof Error) ? newError : Error(mesg);
      });
  }

  patch(id, payload) {
    return this.initialize()
      .then(() => axios.patch(`${this.baseUrl}/${id}`, payload))
      .then((resp) => {
        if (resp && resp.data) return resp.data;

        return Error('No data in update response.');
      });
  }

  setFilterTerm(params, filters = [], searchTerm) {
    const searchFilter = [];

    if (searchTerm && params.filter) {
      const filter = params.filter.find(f => f.operator === 'search');

      if (filter) {
        searchFilter.push({ ...filter, ...{ value: searchTerm } });
      }
    }

    return filters
      .concat(searchFilter)
      .reduce((acc, filter) => {
        const { name, operator, value } = filter;

        if (value !== '') {
          acc.push(`${name}:${operator}:${value}`);
        }

        return acc;
      }, [])
      .join(',') || '';
  }

  setSearchTerm(params, searchTerm) {
    if (searchTerm) {
      if (params && params.search) {
        // eslint-disable-next-line no-param-reassign
        params.search.searchTerm = searchTerm;
      }
    } else if (params) {
      // eslint-disable-next-line no-param-reassign
      delete params.search;
    }
  }
}
